import { cn } from '@/lib/utils'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons, type DsIcon } from '@/ds/icons'

export interface DsChipProps {
  label: string
  selected?: boolean
  onClick?: () => void
  leadingIcon?: DsIcon
  count?: number
  onDelete?: () => void
  size?: 'sm' | 'md'
  semanticLabel?: string
  className?: string
}

export function DsChip({ label, selected, onClick, leadingIcon, count, onDelete, size = 'md', semanticLabel, className }: DsChipProps) {
  const Tag = onClick ? 'button' : 'span'
  return (
    <span className={cn('inline-flex min-h-tap items-center', className)}>
      <Tag
        type={onClick ? 'button' : undefined}
        onClick={onClick}
        aria-pressed={onClick ? !!selected : undefined}
        aria-label={semanticLabel}
        className={cn(
          'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-small-strong outline-none transition-colors motion-fast focus-visible:ring-focus',
          size === 'sm' ? 'h-8' : 'h-9',
          selected ? 'border-brand/60 bg-accent text-accent-foreground' : 'border-border bg-card text-foreground hover:bg-muted',
        )}
      >
        {leadingIcon && <DsIconGlyph icon={leadingIcon} size={16} />}
        {label}
        {count !== undefined && (
          <span className={cn('rounded-full px-1.5 text-caption font-semibold tabular-nums', selected ? 'bg-brand-vivid/20' : 'bg-muted text-muted-foreground')}>{count}</span>
        )}
        {onDelete && (
          <button type="button" aria-label={`Remove ${label}`} onClick={(e) => { e.stopPropagation(); onDelete() }} className="-mr-1 inline-flex size-5 items-center justify-center rounded-full hover:bg-muted">
            <DsIconGlyph icon={DsIcons.close} size={12} />
          </button>
        )}
      </Tag>
    </span>
  )
}

export interface DsFilterOption<T> {
  value: T
  label: string
  count?: number
  icon?: DsIcon
}

export interface DsFilterBarProps<T> {
  options: DsFilterOption<T>[]
  value: T
  onChange: (value: T) => void
  trailing?: React.ReactNode
  className?: string
  semanticLabel?: string
}

export function DsFilterBar<T extends string | number>({ options, value, onChange, trailing, className, semanticLabel = 'Filter' }: DsFilterBarProps<T>) {
  return (
    // min-w-0 here and on the chip row: a flex item defaults to its content's width, so inside a
    // flex parent (DsToolbar) the chips widened the page on a phone instead of scrolling.
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      <div role="group" aria-label={semanticLabel} className="-mx-gutter flex min-w-0 flex-1 gap-2 overflow-x-auto px-gutter [scrollbar-width:none] md:mx-0 md:flex-wrap md:px-0">
        {options.map((o) => (
          <DsChip key={String(o.value)} label={o.label} count={o.count} leadingIcon={o.icon} selected={o.value === value} onClick={() => onChange(o.value)} />
        ))}
      </div>
      {trailing}
    </div>
  )
}
